/**
 * Environment Utilities
 * Handles environment-specific behavior for development vs production
 */

export class EnvironmentUtils {
  /**
   * Check if we're running in development mode
   */
  static isDevelopment() {
    return process.env.NODE_ENV === 'development' || 
           window.location.hostname === 'localhost' ||
           window.location.hostname === '127.0.0.1';
  }

  /**
   * Check if we're running in production mode
   */
  static isProduction() {
    return !this.isDevelopment();
  }

  /**
   * Get the current port from the browser location
   */
  static getCurrentPort() {
    return window.location.port || (window.location.protocol === 'https:' ? '443' : '80');
  }

  /**
   * Get the debug backend URL from localStorage (set by debug panel)
   */
  static getDebugBackendUrl() {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('debug_base_url');
  }

  /**
   * Check if debug panel has backend set to production
   */
  static isDebugBackendProduction() {
    const debugUrl = this.getDebugBackendUrl();
    return debugUrl && (
      debugUrl.includes('trackable-gpt-backend.onrender.com') ||
      debugUrl.includes('staging.onrender.com')
    );
  }

  /**
   * Get the appropriate base URL for links based on environment
   */
  static getBaseUrl() {
    if (this.isDevelopment()) {
      const currentPort = this.getCurrentPort();
      return `http://localhost:${currentPort}`;
    }
    return 'https://threadshare.ai';
  }

  /**
   * Convert a short URL to the appropriate environment URL
   * In development: converts to localhost with current port
   * In production: replaces old domain with threadshare.ai
   */
  static getEnvironmentUrl(shortUrl) {
    if (!shortUrl) return null;
    
    // Priority 1: Check if debug mode is forcing production backend
    if (this.isDebugBackendProduction()) {
      return this.updateDomainToThreadShare(shortUrl);
    }
    
    // Priority 2: Development environment uses localhost
    if (this.isDevelopment()) {
      // Extract the slug from the short URL
      const urlParts = shortUrl.split('/');
      const slug = urlParts[urlParts.length - 1];
      
      // Get current port dynamically
      const currentPort = this.getCurrentPort();
      
      // Return localhost version with current port
      return `http://localhost:${currentPort}/summary/${slug}`;
    }
    
    // Priority 3: Production environment
    return this.updateDomainToThreadShare(shortUrl);
  }

  /**
   * Update old domain to threadshare.ai in URLs
   * Handles migration from old domains to ThreadShare
   */
  static updateDomainToThreadShare(url) {
    if (!url) return null;
    
    // Replace any old domains with threadshare.ai
    const oldDomains = [
      'www.chattracker.com',
      'llmoverview.com'
    ];
    
    let updatedUrl = url;
    oldDomains.forEach(oldDomain => {
      const httpPattern = new RegExp(`http://${oldDomain.replace('.', '\\.')}`, 'gi');
      const httpsPattern = new RegExp(`https://${oldDomain.replace('.', '\\.')}`, 'gi');
      
      updatedUrl = updatedUrl.replace(httpPattern, 'https://threadshare.ai');
      updatedUrl = updatedUrl.replace(httpsPattern, 'https://threadshare.ai');
    });
    
    return updatedUrl;
  }

  /**
   * Update old domain to threadshare.ai in URLs for sharing functionality
   * Handles migration from old domains to main domain
   */
  static updateDomainToShareThreadShare(url) {
    if (!url) return null;
    
    // Replace any old domains with threadshare.ai
    const oldDomains = [
      'www.chattracker.com',
      'share.threadshare.ai',
      'llmoverview.com'
    ];
    
    let updatedUrl = url;
    oldDomains.forEach(oldDomain => {
      const httpPattern = new RegExp(`http://${oldDomain.replace('.', '\\.')}`, 'gi');
      const httpsPattern = new RegExp(`https://${oldDomain.replace('.', '\\.')}`, 'gi');
      
      updatedUrl = updatedUrl.replace(httpPattern, 'https://threadshare.ai');
      updatedUrl = updatedUrl.replace(httpsPattern, 'https://threadshare.ai');
    });
    
    return updatedUrl;
  }

  /**
   * Get environment-specific summary URL
   */
  static getSummaryUrl(slug) {
    const baseUrl = this.getBaseUrl();
    return `${baseUrl}/summary/${slug}`;
  }

  /**
   * Log environment information (for debugging)
   */
  static logEnvironmentInfo() {
    console.log('🌍 Environment Info:', {
      isDevelopment: this.isDevelopment(),
      isProduction: this.isProduction(),
      baseUrl: this.getBaseUrl(),
      hostname: window.location.hostname,
      port: this.getCurrentPort(),
      fullLocation: window.location.href,
      nodeEnv: process.env.NODE_ENV
    });
  }
}

export default EnvironmentUtils;
